import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { toZonedTime } from 'date-fns-tz';
import { endOfDay, startOfDay, parse } from 'date-fns';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormNumberInput from '../../../components/form/FormNumberInput';
import FormToggle from '../../../components/form/FormToggle';
import FormDateInput from '../../../components/form/FormDateInput';
import { isRedappEnabled } from '../../../utils/redappPop';
import {
  channelOptions,
  formatPayloadTime,
  languageSelectOptions,
  listSectionOptions,
  processTypesOptions,
  webformOptions
} from '../../../utils/common';

import {
  callexAiAgentOptionsGet,
  outboundOptionGet,
  processAdd,
  processDetailsGetById,
  processUpdate,
  scriptsOptionsGet
} from '../../../store/slices/tenantSlices/configurationSlice';
import { useTimezone } from '../../../store/slices/commonSlice';
import { crmsOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  audioPromptsAllOptionGet,
  dynamicIvrOptionsGet,
  ivrOptionsGet
} from '../../../store/slices/tenantSlices/incomingSlice';

// Agent-side switches that mean nothing for a VoiceBroadcast process. They are
// hidden for that type and always forced to their off value.
const voiceBroadcastOffValues = {
  chat_with_user: 'false',
  chat_with_supervisor: 'false',
  is_auto_audit: 'false',
  is_auto_lang: 'en',
  easy_pbx: 'false',
  ai_process: 0,
  ai_number: '',
  is_dispo_required: 'no',
  is_sub_dispo_required: 'no',
  is_sub_sub_dispo_required: 'no',
  sticky_agent: 'false'
};

const AddEditProcessModal = ({
  isOpen,
  onCloseClick,
  isEdit = false,
  editRecord = null,
  setOpenCrmAddModal
}) => {
  const dispatch = useDispatch();
  const timezone = useTimezone();

  const [crmOptions, setCrmOptions] = useState([]);
  const [outboundRoutesOptions, setOutboundRoutesOptions] = useState([]);
  const [scriptOptions, setScriptOptions] = useState([]);
  const [audioPromptOption, setAudioPromptOption] = useState([]);
  const [ivrOption, setIvrOption] = useState([]);
  const [dynamicIvrOption, setDynamicIvrOption] = useState([]);
  const [aiPhoneNumberOption, setAiPhoneNumberOption] = useState([]);

  const today = toZonedTime(new Date(), timezone);

  const initialValues = {
    name: '',
    active: 'true',
    description: '',
    type: '',
    autoWrapup: 0,
    autoMissed: 0,
    autoOutbound: 15,
    dnc: 'true',
    numberMasking: 'true',
    listSection: '',
    script: '',
    webform: [],
    channel: 0,
    taborder: '',
    bufferlevel: 0,
    outboundroutes: '',
    queue: 0,
    crm: '',
    ProgressiveDialingSec: 0,
    agentwisedialing: 'false',
    welcome_prompt: '',
    waiting_prompt: '',
    ring_strategy: 'round_robin',
    forwarding_type: '',
    forwarding_options: '',
    chat_with_user: 'false',
    chat_with_supervisor: 'false',
    machine_detection: 'no',
    is_auto_audit: 'false',
    is_auto_lang: 'en',
    ai_process: 0,
    ai_number: '',
    easy_pbx: 'false',
    is_dispo_required: 'no',
    is_sub_dispo_required: 'no',
    is_sub_sub_dispo_required: 'no',
    sticky_agent: 'false',
    is_redapp: 'false',
    redapp_url: '',
    redapp_msisdn_field: '',
    redapp_fallback_url: '',
    time_zone: 'no',
    start_time: startOfDay(today),
    end_time: endOfDay(today),
    // Per-number daily call limit. Held as yes/no in the form like time_zone and
    // converted to the 0/1 the API expects in handlePayload. 0 on a limit means
    // unlimited; the two limits are independent of each other.
    call_limit: 'no',
    predictive_call_limit: 0,
    manual_call_limit: 0
  };

  const validationSchema = Yup.object({
    name: Yup.string().required('Name is required'),
    type: Yup.string().required('Type is required'),
    outboundroutes: Yup.string().required('Outbound routes is required'),
    crm: Yup.string().required('CRM is required'),
    agentwisedialing: Yup.boolean().required('Agent wise dialing is required'),
    predictive_call_limit: Yup.number()
      .transform((v, o) => (o === '' ? 0 : v))
      .integer('Predictive call limit must be a whole number')
      .min(0, 'Predictive call limit cannot be negative'),
    manual_call_limit: Yup.number()
      .transform((v, o) => (o === '' ? 0 : v))
      .integer('Manual call limit must be a whole number')
      .min(0, 'Manual call limit cannot be negative')
    // autoOutbound: Yup.number()
    //   .required('Auto Outbound is required')
    //   .min(5, 'Auto Outbound should be 5 or more')
  });

  const handlePayload = (values) => {
    const payload = { ...values };
    payload['webform'] = values?.webform?.join(',');
    payload['start_time'] = formatPayloadTime(values.start_time);
    payload['end_time'] = formatPayloadTime(values.end_time);

    // Time based dialing off, so no window is sent at all
    if (values.time_zone === 'no') {
      delete payload['start_time'];
      delete payload['end_time'];
    }

    // Call limit: API stores the toggle as smallint 0/1. When it is off, send 0
    // limits too so a previously saved cap cannot linger on the record.
    payload['call_limit'] = values.call_limit === 'yes' ? 1 : 0;
    if (payload['call_limit'] === 0) {
      payload['predictive_call_limit'] = 0;
      payload['manual_call_limit'] = 0;
    } else {
      payload['predictive_call_limit'] = Number(values.predictive_call_limit) || 0;
      payload['manual_call_limit'] = Number(values.manual_call_limit) || 0;
    }

    // Remove fields based on type
    if (
      ['OutboundAndPredictive', 'OutboundAndPreview', 'OutboundAndProgressive'].includes(
        values.type
      )
    ) {
      delete payload['welcome_prompt'];
      delete payload['waiting_prompt'];
      delete payload['forwarding_options'];
      delete payload['forwarding_type'];
    }

    if (values?.type === 'VoiceBroadcast') {
      Object.assign(payload, voiceBroadcastOffValues);
      delete payload['welcome_prompt'];
      delete payload['waiting_prompt'];
      // delete payload['crm'];
      // delete payload['autoWrapup'];
      // delete payload['autoMissed'];
      // delete payload['dnc'];
      // delete payload['numberMasking'];
    }

    return payload;
  };

  const handleResponse = (response, actionType) => {
    if (response?.message) {
      toast.success(`${actionType} Successfully`);
      onCloseClick(true);
      resetForm();
    } else {
      toast.error(response.error);
    }
  };

  const onSubmit = async () => {
    const actionType = isEdit ? 'Record Updated' : 'Record Added';
    const payload = isEdit
      ? { ...handlePayload(values), id: editRecord.id }
      : handlePayload(values);

    try {
      const action = isEdit ? processUpdate(payload) : processAdd(payload);
      const response = await dispatch(action).unwrap();
      handleResponse(response, actionType);
    } catch (error) {
      console.log('error', error);
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const getCrmsOption = async () => {
    try {
      const response = await dispatch(crmsOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setCrmOptions(options);
      } else {
        setCrmOptions([]);
      }
    } catch (error) {
      setCrmOptions([]);
      console.log('error', error);
    }
  };

  const getIvrOption = async () => {
    try {
      const response = await dispatch(ivrOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setIvrOption(options);
      } else {
        setIvrOption([]);
      }
    } catch (error) {
      setIvrOption([]);
      console.log('error', error);
    }
  };

  const getDynamicIvrOption = async () => {
    try {
      const response = await dispatch(dynamicIvrOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.ivr_name,
          value: val.id
        }));
        setDynamicIvrOption(options);
      } else {
        setDynamicIvrOption([]);
      }
    } catch (error) {
      setDynamicIvrOption([]);
      console.log('error', error);
    }
  };

  const getAIAgentOption = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id) || ""
        }));
        setAiPhoneNumberOption(options);
      } else {
        setAiPhoneNumberOption([]);
      }
    } catch (error) {
      setAiPhoneNumberOption([]);
      console.log('error', error);
    }
  };

  const getScriptOption = async () => {
    try {
      const response = await dispatch(scriptsOptionsGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: String(val.id)
        }));
        setScriptOptions(options);
      } else {
        setScriptOptions([]);
      }
    } catch (error) {
      setScriptOptions([]);
      console.log('error', error);
    }
  };

  const getOutboundRoutesOption = async () => {
    try {
      const response = await dispatch(outboundOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setOutboundRoutesOptions(options);
      } else {
        setOutboundRoutesOptions([]);
      }
    } catch (error) {
      setOutboundRoutesOptions([]);
      console.log('error', error);
    }
  };

  const getAudioPromptOption = async () => {
    try {
      const response = await dispatch(audioPromptsAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setAudioPromptOption(options);
      } else {
        setAudioPromptOption([]);
      }
    } catch (error) {
      setAudioPromptOption([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      getScriptOption();
      getCrmsOption();
      getAudioPromptOption();
      getOutboundRoutesOption();
      getIvrOption();
      getDynamicIvrOption();
      getAIAgentOption();
    }
  }, [isOpen]);

  const parseTime = (timeString) => {
    if (timeString.includes(' ')) {
      const timeOnly = timeString.split(' ')[1];
      return parse(timeOnly, 'HH:mm:ss', today);
    }
    return parse(timeString, 'HH:mm', today);
  };

  const fetchProcessRecord = async () => {
    try {
      const response = await dispatch(processDetailsGetById(editRecord?.id)).unwrap();
      setValues({
        ...response,
        webform: response?.webform?.split(',') || [],
        active: String(response?.active),
        dnc: String(response?.dnc) || 'true',
        numberMasking: String(response?.numberMasking),
        agentwisedialing: String(response?.agentwisedialing),
        outboundroutes: Number(response?.outboundroutes) || '',
        forwarding_options: Number(response?.forwarding_options) || '',
        chat_with_supervisor: String(response?.chat_with_supervisor) || 'false',
        chat_with_user: String(response?.chat_with_user) || 'false',
        is_auto_audit: String(response?.is_auto_audit) || 'false',
        machine_detection: String(response?.machine_detection) || 'no',
        is_auto_lang: response?.is_auto_lang || 'en',
        ai_process: response?.ai_process || 0,
        ai_number: response?.ai_number || '',
        easy_pbx: String(response?.easy_pbx) || 'false',
        is_dispo_required: String(response?.is_dispo_required) || 'no',
        is_sub_dispo_required: String(response?.is_sub_dispo_required) || 'no',
        is_sub_sub_dispo_required: String(response?.is_sub_sub_dispo_required) || 'no',
        sticky_agent: String(response?.sticky_agent) || 'false',
        ring_strategy: response?.ring_strategy || 'round_robin',
        is_redapp: String(response?.is_redapp) || 'false',
        redapp_url: response?.redapp_url || '',
        redapp_msisdn_field: response?.redapp_msisdn_field || '',
        redapp_fallback_url: response?.redapp_fallback_url || '',
        time_zone: response?.time_zone === 'yes' ? 'yes' : 'no',
        start_time: response?.start_time ? parseTime(response.start_time) : startOfDay(today),
        end_time: response?.end_time ? parseTime(response.end_time) : endOfDay(today),
        call_limit: Number(response?.call_limit) === 1 ? 'yes' : 'no',
        predictive_call_limit: Number(response?.predictive_call_limit) || 0,
        manual_call_limit: Number(response?.manual_call_limit) || 0,
        ...(response?.type === 'VoiceBroadcast' ? voiceBroadcastOffValues : {})
      });
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchProcessRecord();
    }
  }, [editRecord]);

  const getForwardingOptions = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_playback') {
      return audioPromptOption;
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return ivrOption;
    }
    if (forwarding_type === 'transfer_to_ivr_dynamic') {
      return dynamicIvrOption;
    }
    return [];
  };

  const getForwardingLabel = (forwarding_type) => {
    if (forwarding_type === 'transfer_to_playback') {
      return 'Playback';
    }
    if (forwarding_type === 'transfer_to_ivr') {
      return 'IVR';
    }
    if (forwarding_type === 'transfer_to_ivr_dynamic') {
      return 'Dynamic IVR';
    }
    return '';
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Process' : 'Add Process'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="active"
                label="Active"
                value={values.active}
                onChange={(e) => setFieldValue('active', e)}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="crm"
                label="CRM"
                placeholder="Select CRM"
                value={values.crm}
                options={crmOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isAdd={true}
                createName="Add CRMS"
                onCreateClick={() => {
                  setOpenCrmAddModal(true);
                  onCloseClick();
                }}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="channel"
                label="Dial Level"
                placeholder="Select Dial Level"
                value={values.channel}
                options={channelOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormNumberInput
                name="bufferlevel"
                label="Buffer Level"
                value={values.bufferlevel}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                min={0}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="description"
                label="Description"
                placeholder="Enter description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="type"
                label="Type"
                placeholder="Select Type"
                value={values.type}
                options={
                  process.env.REACT_APP_VOICE_BROADCAST === 'true'
                    ? [
                        ...processTypesOptions,
                        {
                          label: 'Broadcast',
                          options: [{ label: 'VoiceBroadcast', value: 'VoiceBroadcast' }]
                        }
                      ]
                    : [...processTypesOptions]
                }
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue('agentwisedialing', 'false');
                  setFieldValue('ProgressiveDialingSec', 0);
                  setFieldValue('welcome_prompt', '');
                  setFieldValue('waiting_prompt', '');
                  setFieldValue('forwarding_type', '');
                  setFieldValue('forwarding_options', '');
                  if (e.target.value === 'VoiceBroadcast') {
                    setFieldValue('script', '');
                    setFieldValue('taborder', '');
                    setFieldValue('webform', []);
                    setFieldValue('autoWrapup', 0);
                    setFieldValue('queue', 0);
                    setFieldValue('channel', 0);
                    setFieldValue('autoMissed', 0);
                    setFieldValue('autoOutbound', 15);
                    setFieldValue('dnc', 'true');
                    setFieldValue('numberMasking', 'true');
                    Object.entries(voiceBroadcastOffValues).forEach(([key, val]) =>
                      setFieldValue(key, val)
                    );
                  }
                }}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                isGroup
                menuPlacement="bottom"
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="autoWrapup"
                  label="Auto Wrapup"
                  value={values.autoWrapup}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="autoMissed"
                  label="Auto Missed"
                  value={values.autoMissed}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="autoOutbound"
                  label="Auto Outbound"
                  value={values.autoOutbound}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            <Col lg="3">
              <FormToggle
                name="dnc"
                label="DNC"
                value={values.dnc}
                onChange={(e) => setFieldValue('dnc', e)}
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormToggle
                  name="numberMasking"
                  label="Number Masking"
                  value={values.numberMasking}
                  onChange={(e) => setFieldValue('numberMasking', e)}
                />
              </Col>
            )}
            <Col lg="3">
              <FormSelect
                name="listSection"
                label="List Section"
                placeholder="Select List Section"
                value={values.listSection}
                options={listSectionOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormSelect
                  name="script"
                  label="Script"
                  placeholder="Select Script"
                  value={values.script}
                  options={scriptOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormSelect
                  name="webform"
                  label="Webform"
                  placeholder="Select Webform"
                  value={values.webform}
                  options={webformOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  isMulti
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormInput
                  name="taborder"
                  label="Tab Order"
                  placeholder="Enter tab order"
                  value={values.taborder}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            <Col lg="3">
              <FormSelect
                name="outboundroutes"
                label="Outbound Routes"
                placeholder="Select Outbound Routes"
                value={values.outboundroutes}
                options={outboundRoutesOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <Col lg="3">
                <FormNumberInput
                  name="queue"
                  label="queue"
                  value={values.queue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.type !== '' &&
              (values.type === 'BlendedAndProgressive' ||
                values.type === 'OutboundAndProgressive') && (
                <Col lg="3">
                  <FormNumberInput
                    name="ProgressiveDialingSec"
                    label="Progressive Dialing Sec"
                    value={values.ProgressiveDialingSec}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    touched={touched}
                    errors={errors}
                    min={0}
                  />
                </Col>
              )}
            {values.type !== '' &&
              (values.type === 'OutboundAndPreview' ||
                values.type === 'OutboundAndProgressive' ||
                values.type === 'BlendedAndPreview' ||
                values.type === 'BlendedAndProgressive') && (
                <Col lg="3">
                  <FormToggle
                    name="agentwisedialing"
                    label="Agent Wise Dialing"
                    value={values.agentwisedialing}
                    onChange={(e) => setFieldValue('agentwisedialing', e)}
                  />
                </Col>
              )}
            {values.type !== 'VoiceBroadcast' && (
              <>
                <Col lg="3">
                  <FormToggle
                    name="chat_with_user"
                    label="Chat with user"
                    value={values.chat_with_user}
                    onChange={(e) => setFieldValue('chat_with_user', e)}
                  />
                </Col>
                <Col lg="3">
                  <FormToggle
                    name="chat_with_supervisor"
                    label="Chat with supervisor"
                    value={values.chat_with_supervisor}
                    onChange={(e) => setFieldValue('chat_with_supervisor', e)}
                  />
                </Col>
                <Col lg="3">
                  <FormToggle
                    name="is_auto_audit"
                    label="Auto Audit"
                    value={values.is_auto_audit}
                    onChange={(e) => setFieldValue('is_auto_audit', e)}
                  />
                </Col>
              </>
            )}
            {isRedappEnabled() && (
              <>
            <Col lg="3">
              <FormToggle
                name="is_redapp"
                label="RedApp"
                value={values.is_redapp}
                onChange={(e) => setFieldValue('is_redapp', e)}
              />
            </Col>
            {values.is_redapp === 'true' && (
              <Col lg="3">
                <FormInput
                  name="redapp_url"
                  label="RedApp URL"
                  placeholder="https://www.redapp.vodafoneidea.com:1818/Disposal/Dialer"
                  value={values.redapp_url}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.is_redapp === 'true' && (
              <Col lg="3">
                <FormInput
                  name="redapp_msisdn_field"
                  label="RedApp Number Field"
                  placeholder="5 or custom_field_5 (blank = primary number)"
                  value={values.redapp_msisdn_field}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.is_redapp === 'true' && (
              <Col lg="3">
                <FormInput
                  name="redapp_fallback_url"
                  label="RedApp Fallback URL"
                  placeholder="Number na mile to is URL par khulega"
                  value={values.redapp_fallback_url}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
              </>
            )}
            <Col lg="3">
              <FormToggle
                name="machine_detection"
                label="Machine Detection"
                value={String(values.machine_detection === 'yes')}
                onChange={() => {
                  setFieldValue(
                    'machine_detection',
                    values.machine_detection === 'yes' ? 'no' : 'yes'
                  );
                }}
              />
            </Col>
            {values.type !== 'VoiceBroadcast' && (
              <>
                <Col lg="3">
                  <FormToggle
                    name="easy_pbx"
                    label="Easy PBX"
                    value={values.easy_pbx}
                    onChange={(e) => setFieldValue('easy_pbx', e)}
                  />
                </Col>
                <Col lg="3">
                  <FormToggle
                    name="ai_process"
                    label="AI Process"
                    value={String(values.ai_process === 1)}
                    onChange={() => {
                      setFieldValue('ai_process', values.ai_process === 1 ? 0 : 1);
                      if (values.ai_process === 1) {
                        setFieldValue('ai_number', '');
                      }
                    }}
                  />
                </Col>
              </>
            )}
            {values.ai_process === 1 && (
              <Col lg="3">
                <FormSelect
                  name="ai_number"
                  label="AI Agent"
                  placeholder="Select AI Agent"
                  value={values.ai_number}
                  options={aiPhoneNumberOption}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.is_auto_audit === 'true' && (
              <Col lg="3">
                <FormSelect
                  name="is_auto_lang"
                  label="Auto Language"
                  placeholder="Select auto language"
                  value={values.is_auto_lang}
                  options={languageSelectOptions}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.type !== 'VoiceBroadcast' && (
              <>
                <Col lg="3">
                  <FormToggle
                    name="is_dispo_required"
                    label="Disposition Mandatory"
                    value={String(values.is_dispo_required === 'yes')}
                    onChange={() => {
                      setFieldValue(
                        'is_dispo_required',
                        values.is_dispo_required === 'yes' ? 'no' : 'yes'
                      );
                    }}
                  />
                </Col>
                <Col lg="3">
                  <FormToggle
                    name="is_sub_dispo_required"
                    label="Sub Disposition Mandatory"
                    value={String(values.is_sub_dispo_required === 'yes')}
                    onChange={() => {
                      setFieldValue(
                        'is_sub_dispo_required',
                        values.is_sub_dispo_required === 'yes' ? 'no' : 'yes'
                      );
                    }}
                  />
                </Col>
                <Col lg="3">
                  <FormToggle
                    name="is_sub_sub_dispo_required"
                    label="Sub Sub Disposition Mandatory"
                    value={String(values.is_sub_sub_dispo_required === 'yes')}
                    onChange={() => {
                      setFieldValue(
                        'is_sub_sub_dispo_required',
                        values.is_sub_sub_dispo_required === 'yes' ? 'no' : 'yes'
                      );
                    }}
                  />
                </Col>
                <Col lg="3">
                  <FormToggle
                    name="sticky_agent"
                    label="Sticky Agent"
                    value={values.sticky_agent}
                    onChange={(e) => setFieldValue('sticky_agent', e)}
                  />
                </Col>
              </>
            )}
            <Col lg="3">
              <FormToggle
                trueName="Yes"
                falseName="No"
                name="time_zone"
                label="Time Based"
                value={String(values.time_zone === 'yes')}
                onChange={() => {
                  setFieldValue('time_zone', values.time_zone === 'yes' ? 'no' : 'yes');
                  if (values.time_zone === 'no') {
                    setFieldValue('start_time', startOfDay(today));
                    setFieldValue('end_time', endOfDay(today));
                  }
                }}
              />
            </Col>
            {values.time_zone === 'yes' && (
              <Col lg="3">
                <FormDateInput
                  label="Start Time"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  showTimeSelect={true}
                  name="start_time"
                  timeIntervals="1"
                  value={values.start_time}
                  showTimeSelectOnly={true}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            {values.time_zone === 'yes' && (
              <Col lg="3">
                <FormDateInput
                  label="End Time"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  showTimeSelect={true}
                  name="end_time"
                  timeIntervals="1"
                  value={values.end_time}
                  showTimeSelectOnly={true}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
            )}
            <Col lg="3">
              <FormToggle
                trueName="Yes"
                falseName="No"
                name="call_limit"
                label="Call Limit"
                value={String(values.call_limit === 'yes')}
                onChange={() => {
                  const next = values.call_limit === 'yes' ? 'no' : 'yes';
                  setFieldValue('call_limit', next);
                  // Turning it off clears the caps so a stale value is never resent
                  if (next === 'no') {
                    setFieldValue('predictive_call_limit', 0);
                    setFieldValue('manual_call_limit', 0);
                  }
                }}
              />
            </Col>
            {values.call_limit === 'yes' && (
              <Col lg="3">
                <FormNumberInput
                  name="predictive_call_limit"
                  label="Predictive Call Limit (per number / day)"
                  value={values.predictive_call_limit}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
            {values.call_limit === 'yes' && (
              <Col lg="3">
                <FormNumberInput
                  name="manual_call_limit"
                  label="Manual Call Limit (per number / day)"
                  value={values.manual_call_limit}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                  min={0}
                />
              </Col>
            )}
          </Row>
          {values.type !== '' &&
            !['OutboundAndPredictive', 'OutboundAndPreview', 'OutboundAndProgressive'].includes(
              values.type
            ) && (
              <React.Fragment>
                <div className="w-100 py-2 px-3 bg-primary text-white fw-bold d-flex justify-content-between align-items-center">
                  <div>
                    {values.type === 'VoiceBroadcast'
                      ? 'Broadcast Configuration'
                      : 'Incoming Configuration'}
                  </div>
                </div>
                {values.type === 'VoiceBroadcast' ? (
                  <Row className="py-2">
                    <Col lg="3" md="3" className="flex-row">
                      <FormSelect
                        name="forwarding_type"
                        label="Forwarding Type"
                        placeholder="Select Forwarding Type"
                        value={values.forwarding_type}
                        options={[
                          { label: 'Transfer To IVR', value: 'transfer_to_ivr' },
                          { label: 'Transfer To Dynamic IVR', value: 'transfer_to_ivr_dynamic' },
                          { label: 'Playback', value: 'transfer_to_playback' }
                        ]}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value === 'transfer_to_hangup') {
                            setFieldValue('forwarding_options', 'Hangup');
                          } else {
                            setFieldValue('forwarding_options', '');
                          }
                        }}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    {values.forwarding_type !== '' ? (
                      <Col lg="3" md="3" className="flex-row">
                        <FormSelect
                          name="forwarding_options"
                          label={getForwardingLabel(values.forwarding_type)}
                          placeholder={`Select ${getForwardingLabel(values.forwarding_type)}`}
                          value={values.forwarding_options}
                          options={getForwardingOptions(values.forwarding_type)}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          touched={touched}
                          errors={errors}
                        />
                      </Col>
                    ) : null}
                  </Row>
                ) : (
                  <Row className="py-2">
                    <Col lg="3">
                      <FormSelect
                        name="welcome_prompt"
                        label="Welcome Prompt"
                        placeholder="Select Welcome Prompt"
                        value={values.welcome_prompt}
                        options={audioPromptOption}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    <Col lg="3">
                      <FormSelect
                        name="waiting_prompt"
                        label="Waiting Prompt"
                        placeholder="Select Waiting Prompt"
                        value={values.waiting_prompt}
                        options={audioPromptOption}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                      />
                    </Col>
                    {(values.type === 'BlendedAndPreview' ||
                      values.type === 'BlendedAndPredictive' ||
                      values.type === 'BlendedAndProgressive' ||
                      values.type === 'InboundAndManual') && (
                      <Col lg="3">
                        <FormToggle
                          name="ring_strategy"
                          label="Ring Strategy"
                          trueName="Call All"
                          falseName="Round Robin"
                          value={values.ring_strategy === 'call_all' ? 'true' : 'false'}
                          onChange={(e) =>
                            setFieldValue(
                              'ring_strategy',
                              e === 'true' ? 'call_all' : 'round_robin'
                            )
                          }
                        />
                      </Col>
                    )}
                  </Row>
                )}
              </React.Fragment>
            )}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditProcessModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any,
  setOpenCrmAddModal: PropTypes.func
};

export default AddEditProcessModal;
